import type { AddOn, ConfiguredAddOn } from '../types/addon.types';
import type { ConfiguredService } from '../types/service.types';
import { toDateKey } from '@features/quote/utils/dateKey';
import { toLineDescription } from './lineDescription';
import { isMandatoryAddOnLineForLink } from './autoIncludedAddOns';
import type { InstanceIdMinter } from './instanceIds';

export function buildAddOnInstances(
    addOn: AddOn,
    schedule: string,
    date: Date | null,
    services: ConfiguredService[],
    mint: InstanceIdMinter
): ConfiguredAddOn[] {
    const startDate = schedule === 'independent' && date ? toDateKey(date) : null;

    if (schedule === 'independent' && startDate === null) {
        return [];
    }

    if (schedule === 'eachservice') {
        return services.map((service) => ({
            instanceId: mint(),
            addOnId: addOn.id,
            name: addOn.name,
            description: toLineDescription(addOn.description),
            price: addOn.price,
            gross_price: addOn.gross_price,
            linkedServiceId: service.instanceId,
            startDate,
            source: 'manual' as const,
            mandatory: false,
        }));
    }

    return [
        {
            instanceId: mint(),
            addOnId: addOn.id,
            name: addOn.name,
            description: toLineDescription(addOn.description),
            price: addOn.price,
            gross_price: addOn.gross_price,
            // A stored independent line is null; 'independent' is a form-only sentinel.
            linkedServiceId: schedule === 'independent' ? null : schedule,
            startDate,
            source: 'manual',
            mandatory: false,
        },
    ];
}

export function scheduleOptionsFor(
    addOnId: string,
    services: ConfiguredService[],
    instances: ConfiguredAddOn[]
): { value: string; label: string }[] {
    const options = [{ value: 'independent', label: 'Independent' }];

    if (!isMandatoryAddOnLineForLink(addOnId, 'eachservice', instances)) {
        options.push({ value: 'eachservice', label: 'With each service' });
    }

    services.forEach((service, index) => {
        if (!isMandatoryAddOnLineForLink(addOnId, service.instanceId, instances)) {
            options.push({
                value: service.instanceId,
                label: `With Service #${index + 1} (${service.name})`,
            });
        }
    });

    return options;
}

export interface InstanceOrdinal {
    index: number;
    count: number;
}

// Rows of the same add-on share a name, so each one carries its position among them.
export function instanceOrdinals(instances: ConfiguredAddOn[]): InstanceOrdinal[] {
    const totals = new Map<string, number>();
    instances.forEach((entry) => totals.set(entry.addOnId, (totals.get(entry.addOnId) ?? 0) + 1));

    const seen = new Map<string, number>();

    return instances.map((entry) => {
        const index = seen.get(entry.addOnId) ?? 0;
        seen.set(entry.addOnId, index + 1);
        return { index, count: totals.get(entry.addOnId) ?? 1 };
    });
}
